import { DEFAULT_ALGOLIA_INDEX_NAME } from "@workspace/algolia-contract";

export const ALGOLIA_APP_ID = import.meta.env.VITE_ALGOLIA_APP_ID as string | undefined;
export const ALGOLIA_SEARCH_KEY = import.meta.env.VITE_ALGOLIA_SEARCH_KEY as string | undefined;
export const ALGOLIA_INDEX_NAME = (import.meta.env.VITE_ALGOLIA_INDEX_NAME as string) || DEFAULT_ALGOLIA_INDEX_NAME;

export function shouldEnableAlgolia(input: {
  appId?: string;
  searchKey?: string;
  isProduction: boolean;
  appDbMode?: string;
  enableDevAlgolia?: string;
}): boolean {
  if (!input.appId || !input.searchKey) return false;

  // Replit Secrets are shared across development and deployment. The search
  // credentials currently point at the production index, while the normal dev
  // API points at the isolated dev database. Enabling that index in `vite`
  // preview produces search cards for production-only cars whose detail API
  // must then return 404. Normal dev therefore uses the existing REST fallback
  // against its own database. Production and the explicit prod-readonly preview
  // may use the production index; a future isolated dev index can opt in.
  return input.isProduction
    || input.appDbMode === "prod-readonly"
    || input.enableDevAlgolia === "1";
}

export const isAlgoliaConfigured = shouldEnableAlgolia({
  appId: ALGOLIA_APP_ID,
  searchKey: ALGOLIA_SEARCH_KEY,
  isProduction: import.meta.env.PROD,
  appDbMode: import.meta.env.VITE_APP_DB_MODE,
  enableDevAlgolia: import.meta.env.VITE_ENABLE_DEV_ALGOLIA,
});

type CachedIndexState = { valid: true; expiresAt: number } | null;

let _indexState: CachedIndexState = null;

// Persist the health-check result for the browser tab so subsequent
// navigations to /search don't pay another ~200-500ms Algolia round-trip
// to validate the index. The cache is intentionally short-lived
// (sessionStorage, not localStorage) so a tab restart re-validates, and
// we attach a TTL so an Algolia outage mid-session is detected within
// CACHE_TTL_MS rather than being pinned to the cached state for the
// life of the tab.
const SESSION_CACHE_KEY = "bac:algolia-index-valid";
const CACHE_TTL_MS = 10 * 60 * 1000;

function readCachedIndexState(): CachedIndexState {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { valid?: unknown; expiresAt?: unknown };
    if (parsed?.valid !== true) return null;
    if (typeof parsed.expiresAt !== "number") return null;
    if (Date.now() >= parsed.expiresAt) {
      try { sessionStorage.removeItem(SESSION_CACHE_KEY); } catch { /* ignore */ }
      return null;
    }
    return { valid: true, expiresAt: parsed.expiresAt };
  } catch {
    return null;
  }
}

// Synchronous cache lookup for use during initial render so we can skip
// the skeleton entirely on subsequent /search visits within the same
// browser tab. Returns true when the cache is still fresh, otherwise
// null so the caller renders the skeleton and revalidates.
export function getCachedAlgoliaIndexValid(): boolean | null {
  if (_indexState && Date.now() < _indexState.expiresAt) return true;
  _indexState = readCachedIndexState();
  return _indexState ? true : null;
}

function writeCachedIndexValid(): void {
  if (typeof sessionStorage === "undefined") return;
  try {
    const expiresAt = Date.now() + CACHE_TTL_MS;
    sessionStorage.setItem(
      SESSION_CACHE_KEY,
      JSON.stringify({ valid: true, expiresAt }),
    );
    _indexState = { valid: true, expiresAt };
  } catch {
    // sessionStorage may throw in private mode / quota — ignore.
  }
}

// Hard ceiling on the Algolia health-check round-trip. Without this, a
// hung connection (corporate proxy, ChromeOS captive network, blocked
// CSP, browser extension) leaves `checkAlgoliaIndex` pending forever
// — and SearchPage stays gated on `algoliaReady === null`, rendering
// the skeleton indefinitely (the bug behind task #2267). We'd rather
// fall back to the slower Supabase path than show a permanent skeleton.
const ALGOLIA_HEALTH_TIMEOUT_MS = 8000;

export async function checkAlgoliaIndex(): Promise<boolean> {
  if (_indexState && Date.now() < _indexState.expiresAt) return true;
  const cached = readCachedIndexState();
  if (cached) {
    _indexState = cached;
    return true;
  }
  if (!isAlgoliaConfigured || !ALGOLIA_APP_ID || !ALGOLIA_SEARCH_KEY) {
    return false;
  }
  try {
    const { algoliasearch } = await import("algoliasearch");
    const client = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);
    const probe = client.searchSingleIndex({
      indexName: ALGOLIA_INDEX_NAME,
      searchParams: { query: "", hitsPerPage: 0 },
    });
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<never>((_, reject) => {
      timeoutHandle = setTimeout(
        () => reject(new Error(`Algolia health-check timed out after ${ALGOLIA_HEALTH_TIMEOUT_MS}ms`)),
        ALGOLIA_HEALTH_TIMEOUT_MS,
      );
    });
    try {
      await Promise.race([probe, timeout]);
    } finally {
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
    }
    writeCachedIndexValid();
    return true;
  } catch (err) {
    console.warn(
      `[Algolia] Index "${ALGOLIA_INDEX_NAME}" not available, falling back to database search`,
      err,
    );
    // Do NOT cache a negative result — a transient network blip on first
    // visit shouldn't pin the user to the slower Supabase fallback for
    // the entire session, and we want the next /search visit to re-probe.
    return false;
  }
}

export async function getAlgoliaFacetValues(facetName: string): Promise<string[]> {
  if (!isAlgoliaConfigured || !ALGOLIA_APP_ID || !ALGOLIA_SEARCH_KEY) return [];

  try {
    const { algoliasearch } = await import("algoliasearch");
    const client = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);

    const response = await client.searchSingleIndex({
      indexName: ALGOLIA_INDEX_NAME,
      searchParams: {
        query: "",
        facets: [facetName],
        hitsPerPage: 0,
      },
    });

    const facets = response.facets?.[facetName];
    if (!facets) return [];
    return Object.keys(facets).sort();
  } catch {
    return [];
  }
}

/**
 * Read globally ranked inventory counts for a facet, without the active
 * search refinements or the current result page affecting popularity.
 */
export async function getAlgoliaFacetCounts(
  facetName: string,
): Promise<Array<{ value: string; count: number }>> {
  if (!isAlgoliaConfigured || !ALGOLIA_APP_ID || !ALGOLIA_SEARCH_KEY) return [];

  try {
    const { algoliasearch } = await import("algoliasearch");
    const client = algoliasearch(ALGOLIA_APP_ID, ALGOLIA_SEARCH_KEY);
    const response = await client.searchSingleIndex({
      indexName: ALGOLIA_INDEX_NAME,
      searchParams: {
        query: "",
        facets: [facetName],
        hitsPerPage: 0,
        maxValuesPerFacet: 1000,
      },
    });
    const facetCounts = response.facets?.[facetName];
    if (!facetCounts) return [];
    return Object.entries(facetCounts)
      .filter((entry): entry is [string, number] => Number.isFinite(entry[1]))
      .map(([value, count]) => ({ value, count }));
  } catch {
    return [];
  }
}
